import React, { useState } from "react";
import {
  Container,
  InputLabel,
  Box,
  FormControl,
  Select,
  MenuItem,
  AlertTitle,
  Alert,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CssBaseline from "@mui/material/CssBaseline";
import DatePicker, {registerLocale} from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fileReportDownload } from "../../../utils/fileReportDownload";
import { dateToFormat } from "../../../utils/dateParsing";
import es from "date-fns/locale/es";

const REPORTS_TYPE = [
  { name: 'Atenciones', url: '/aphcentro_xls_export/attentions', file: 'atenciones' },
  { name: 'Insumos medicamentos', url: '/aphcentro_xls_export/supplies', file: 'Insumos-medicamentos' },
  { name: 'FURTRAN', url: '/aphcentro_xls_export/furtran', file: 'FURTRAN' },
  { name: 'Humedad relativa PDF', url: '/aph_centro_oriente_pdf/relative_humidity_report', file: 'humedad-relativa', showAmbulanceField: true, isPDF:true },
  { name: 'Elementos de protección PDF', url: '/aph_centro_oriente_pdf/protection_elements_tab_report', file: 'elementos-de-protección', showAmbulanceField: true, isPDF:true },
  { name: 'Desinfección PDF', url: '/aph_centro_oriente_pdf/desinfection_report', file: 'desinfeccion', showAmbulanceField: true, ambulanceCode:true, isPDF:true },
];

const AMBULANCE_LIST = [
  {ambulance:'JQV143', mobileCode:'6876'},
  {ambulance:'JQV144', mobileCode:'6877'},
  {ambulance:'JQV145', mobileCode:'6878'},
  {ambulance:'JQV146', mobileCode:'6879'},
  {ambulance:'JQV147', mobileCode:'6875'},
  {ambulance:'JQV148', mobileCode:'6851'},
  {ambulance:'JQV149', mobileCode:'6880'},
  {ambulance:'JQV150', mobileCode:'6852'},
  {ambulance:'JQV222', mobileCode:'6996'},
  {ambulance:'LIS763', mobileCode:'7070'},
  {ambulance:'LIS786', mobileCode:'7050'},
  {ambulance:'LIS787', mobileCode:'7051'},
  {ambulance:'LIS788', mobileCode:'7071'},
  {ambulance:'LIS789', mobileCode:'7072'},
  {ambulance:'LIS790', mobileCode:'7073'},
  {ambulance:'OCK366', mobileCode:'5176'},
  {ambulance:'OLN263', mobileCode:'6339'},
  {ambulance:'OLN264', mobileCode:'6340'},
  {ambulance:'OLN312', mobileCode:'6282'},
  {ambulance:'OLN313', mobileCode:'6281'},
  {ambulance:'OLN317', mobileCode:'6341'},
  {ambulance:'OLN318', mobileCode:'6342'},
];

export const APHCentroOrienteReport = () => {
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [ambulanceSelected, setAmbulanceSelected] = useState({});
  const [filterType, setFilterType] = useState(REPORTS_TYPE[0]);
  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertInfo, setAlertInfo] = useState({});

  registerLocale("es", es);

  const handleSubmit = (event) => {
    event.preventDefault();
    requestReport();
  };

  const requestReport = async () => {
    try {
      const params = {
        fromDate: dateToFormat(fromDate, 'YYYY/MM/DD HH:mm'),
        untilDate: dateToFormat(toDate, 'YYYY/MM/DD HH:mm'),
        ambulance: !filterType.ambulanceCode ? ambulanceSelected.ambulance: ambulanceSelected.mobileCode
      }
      setShowAlert(false);
      setLoading(true);
      const extention = filterType.isPDF?'pdf':'xlsx'
      await fileReportDownload(filterType.url, params, `${filterType.file}.${extention}`,filterType.isPDF);
      setLoading(false);
    } catch (error) {
      console.log("error -> ", error);
      if (error.response.status === 406) { //range bad
        setAlertInfo({
          title: "Error",
          message: "Rango de fecha excedido",
          action: "Cambia el rango de fechas",
        });
        setShowAlert(true);
      } else if (error.response.status === 400) { // rango no válido
        setAlertInfo({
          title: "Error",
          message: "El rango de fecha no es válido",
          action: "Valida que el rango sea correcto",
        });
        setShowAlert(true);
      } else if (error.response.status === 0) {
        setAlertInfo({
          title: "Timeout",
          message: "Ocurrio un error procesando",
          action: "Intenta reduciendo el rango de fechas",
        });
        setShowAlert(true);
      } else {
        setAlertInfo({
          title: "Error del servidor",
          message: "Ocurrio un error procesando",
          action: "Intenta reduciendo el rango de fechas",
        });
        setShowAlert(true);
      }
      setLoading(false);
    }
  };

  const handleChangeSelect = (event) => {
    event.preventDefault();
    setFilterType(event.target.value);
  };

  const handleChangeAmbulanceSelect = (event) => {
    event.preventDefault();
    setAmbulanceSelected(event.target.value)
  };

  return (
    <Container component="main" maxWidth="sm">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
        }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          {showAlert && (
            <Alert onClose={() => setShowAlert(false) } severity="error">
              <AlertTitle>{alertInfo?.title}</AlertTitle>
              {alertInfo?.message} — <strong>{alertInfo?.action}</strong>
            </Alert>
          )}

          <InputLabel>Desde</InputLabel>
          <div style={{ display: "flex", marginBottom: 10 }}>
            <DatePicker
              dateFormat="yyyy/MM/dd HH:mm"
              selected={fromDate}
              onChange={setFromDate}
              showTimeSelect
              name="fromDate"
              locale="es"
              className="react-datetimepicker-custom"
            />
          </div>

          <InputLabel>Hasta</InputLabel>
          <div style={{ display: "flex" }}>
            <DatePicker
              dateFormat="yyyy/MM/dd HH:mm"
              selected={toDate}
              onChange={setToDate}
              showTimeSelect
              name="toDate"
              locale="es"
              className="react-datetimepicker-custom"
            />
          </div>

          <br />
          <FormControl fullWidth>
            <InputLabel id="filtertype-select-label">Filtrar Por</InputLabel>
            <Select
              labelId="filtertype-select-label"
              id="filtertype-select"
              value={filterType}
              label="filterType"
              onChange={handleChangeSelect}
              name="filtertype-select"
            >
              {
                REPORTS_TYPE.map((item, key) => <MenuItem value={item}>{item.name}</MenuItem> )
              }
            </Select>
          </FormControl>

          {
            filterType.showAmbulanceField ?
            <>
              <br/>
              <br/>
              <FormControl fullWidth>
                <InputLabel id="filtertype-ambulance-label">Seleccione la placa</InputLabel>
                <Select
                  labelId="filtertype-ambulance-label"
                  id="filtertype-ambulance"
                  value={ambulanceSelected}
                  label="Seleccione la placa"
                  onChange={handleChangeAmbulanceSelect}
                  name="filtertype-ambulance"
                >
                  {
                    AMBULANCE_LIST.map((item, key) => 
                    <MenuItem key={key+item.ambulance} value={item}>
                      {item.ambulance}
                    </MenuItem>)
                  }
                </Select>
              </FormControl>
            </>
              : null
          }

          <LoadingButton
            type="submit"
            fullWidth
            loading={loading}
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            Generar Reporte
          </LoadingButton>
        </Box>
      </Box>
    </Container>
  );
};
